import {
    Component,
    Input,
    ChangeDetectionStrategy,
    HostListener,
    effect,
    HostBinding,
    Output,
    EventEmitter,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { SidebarStore } from "./s25.sidebar.store";

@TypeManagerDecorator("s25-ng-sidebar")
@Component({
    selector: "s25-ng-sidebar",
    template: `
        @if (mode === "overlay") {
            <button class="aw-button aw-button--outline" (click)="sidebarStore.toggle()">{{ buttonText }}</button>
            <div class="sidebar-overlay" [class.visible]="sidebarStore.isOpen()" (click)="sidebarStore.toggle()"></div>
        }

        <div
            class="sidebar"
            [class.open]="sidebarStore.isOpen()"
            [class.push]="mode === 'push'"
            [style.--sidebar-width.px]="width"
        >
            <div class="sidebar-header">
                <h2>{{ title }}</h2>
                <s25-ng-icon class="s25-modal--close" [type]="'close'" (click)="onClose()"> </s25-ng-icon>
            </div>

            <div class="sidebar-content">
                <ng-content></ng-content>
            </div>
        </div>

        @if (mode === "push" && sidebarStore.tabVisible()) {
            <div class="sidebar-tab" [class.open]="sidebarStore.isOpen()" (click)="sidebarStore.toggle()">
                <s25-ng-icon
                    class="sidebar-tab-icon"
                    [class.open]="sidebarStore.isOpen()"
                    [type]="'doubleArrowLeft'"
                ></s25-ng-icon>
                <s25-ng-icon [type]="'aiSparkles'"></s25-ng-icon>
            </div>
        }
    `,
    styles: [
        `
            :host {
                display: block;
            }

            :host.push {
                height: 100%;
                display: flex;
                flex-direction: row-reverse;
                align-items: flex-start;
                width: 0;
                transition: transform 0.5s ease;
            }

            :host.push.open {
                width: fit-content;
            }

            .sidebar-overlay {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                z-index: 1000;
                opacity: 0;
                visibility: hidden;
                transition:
                    opacity 0.3s ease,
                    visibility 0.3s ease;
            }

            .sidebar-overlay.visible {
                opacity: 1;
                visibility: visible;
            }

            .sidebar {
                position: fixed;
                top: 0;
                background-color: white;
                box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
                z-index: 1001;
                width: var(--sidebar-width, 400px);
                transform: translateX(100%);
                display: flex;
                flex-direction: column;
                transition: transform 0.5s ease;
            }

            .sidebar.open {
                transform: translateX(0);
            }

            .sidebar.push {
                position: sticky;
                top: 10%;
                width: 0;
                overflow: hidden;
            }

            .sidebar.push.open {
                width: var(--sidebar-width, 400px);
            }

            .sidebar-header {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 1rem;
            }

            .sidebar-content {
                flex: 1;
                padding: 1rem;
                overflow-y: auto;
            }

            .sidebar-tab {
                display: grid;
                position: sticky;
                margin-right: -1.5rem;
                top: 10%;
                max-height: fit-content;
                background: var(--cn-blue);
                border: 1px solid rgba(0, 0, 0, 0.08);
                border-radius: 4px 0 0 4px;
                padding: 0.5rem;
                cursor: pointer;
                color: #fff;
                z-index: 1002;

                transition: margin-right 0.5s ease;
            }

            .sidebar-tab.open {
                margin-right: 0;
                transition: transform 0.5s ease;
            }

            .sidebar-tab-icon.open {
                transform: rotate(180deg);
            }
        `,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [S25IconComponent],
})
export class S25SidebarComponent {
    @Input() title = "";
    @Input() buttonText = "";
    @Input() mode: "overlay" | "push" = "overlay";
    @Input() width = 400;

    @Output() closed = new EventEmitter<void>();

    constructor(public sidebarStore: SidebarStore) {}

    onClose() {
        this.closed.emit();
        this.sidebarStore.close();
    }

    @HostListener("document:keydown.escape")
    handleEscape() {
        if (!this.sidebarStore.isOpen()) return;
        this.sidebarStore.close();
    }

    @HostBinding("class.push")
    get pushMode() {
        return this.mode === "push";
    }

    @HostBinding("class.open")
    get isOpen() {
        return this.sidebarStore.isOpen();
    }
}
